// filepath: apps/web/src/features/admin/templates/registry/TemplateRegistryTable.tsx
//
// FR-UI-ADM-11 AC-1 -- table rendering a list of
// TemplateRegistryRow entries.
// FR-UI-ADM-13 AC-1 / AC-3 — adds per-row Export action +
// source badge (Q2 client-side derivation).
//
// Each row shows template_id, version, description, content_hash,
// publisher_kind, pre_vetted, published_at, published_by, a
// source badge (computed client-side via deriveTemplateSource),
// and an Install action that triggers onInstall(row) AND an
// Export action that triggers the bundle download (handled by
// ExportButton). Pre-vetted rows also render a PreVettedBadge
// (informational — see PreVettedBadge.tsx for the FR-TPL-5
// carve-out explanation).
//
// INV-5 contract: the table NEVER renders a manifest_json /
// payload_zip / tenant_id field. The row data flows from
// useTemplateRegistry() which excludes those columns from the
// wire type entirely.

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { formatDate } from "@/i18n/format";
import { ApiError } from "@/definitions/types";
import { InstallButton } from "./InstallButton";
import { PreVettedBadge } from "./PreVettedBadge";
import { classifyTemplateRegistryFetchError } from "./useTemplateRegistry";
import { ExportButton } from "../export-import/ExportButton";
import { deriveTemplateSource } from "../export-import/deriveTemplateSource";
import type { TemplateRegistryRow } from "./types";

interface TemplateRegistryTableProps {
  rows: TemplateRegistryRow[];
  onInstall: (row: TemplateRegistryRow) => void;
  isInstallingRow: string | null;
  installError: ApiError | null;
}

function classifyInstallError(err: ApiError | null): {
  message: string;
  testId: string;
} | null {
  if (!err) return null;
  const classified = classifyTemplateRegistryFetchError(err);
  if (classified.kind === "permission_denied") {
    return {
      message: classified.message,
      testId: "template-registry-install-permission-denied",
    };
  }
  if (classified.kind === "rate_limit") {
    return {
      message: classified.message,
      testId: "template-registry-install-rate-limit",
    };
  }
  return {
    message: classified.message,
    testId: "template-registry-install-fetch-failure",
  };
}

export function TemplateRegistryTable({
  rows,
  onInstall,
  isInstallingRow,
  installError,
}: TemplateRegistryTableProps): React.ReactElement {
  const { t, locale } = useTranslation();
  const errCard = classifyInstallError(installError);

  if (rows.length === 0) {
    return (
      <div
        className="rounded border border-border bg-background p-6 text-center text-sm text-muted-foreground"
        data-testid="template-registry-empty-state"
      >
        {t("admin.templates.registry.empty.message")}
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <div
        className="overflow-x-auto rounded border border-border bg-background"
        data-testid="template-registry-table-wrapper"
      >
        <table
          className="w-full text-sm"
          data-testid="template-registry-table"
          aria-label={t("admin.templates.registry.table.aria_label")}
        >
          <thead>
            <tr className="border-b border-border bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th className="px-3 py-2">
                {t("admin.templates.registry.column.template_id")}
              </th>
              <th className="px-3 py-2">
                {t("admin.templates.registry.column.version")}
              </th>
              <th className="px-3 py-2">
                {t("admin.templates.registry.column.description")}
              </th>
              <th className="px-3 py-2">
                {t("admin.templates.registry.column.publisher_kind")}
              </th>
              <th className="px-3 py-2">
                {t("admin.templates.registry.column.pre_vetted")}
              </th>
              <th className="px-3 py-2">
                {t("admin.templates.registry.column.published_at")}
              </th>
              <th className="px-3 py-2">
                {t("admin.templates.registry.column.published_by")}
              </th>
              <th className="px-3 py-2 text-right">
                {t("admin.templates.registry.column.actions")}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={`${row.template_id}@${row.version}`}
                className="border-b border-border last:border-0"
                data-testid="template-registry-row"
                data-template-id={row.template_id}
                data-version={row.version}
                data-pre-vetted={
                  row.pre_vetted && row.publisher_kind === "platform"
                    ? "true"
                    : "false"
                }
              >
                <td className="px-3 py-2 font-mono text-xs">
                  {row.template_id}
                </td>
                <td className="px-3 py-2 font-mono text-xs">
                  {row.version}
                  {/* FR-UI-ADM-13 AC-3 (Q2) — client-side source
                      derivation. Imported rows carry a 'imported'
                      badge next to the version. */}
                  <span
                    className={
                      deriveTemplateSource(row) === "imported"
                        ? "ml-2 inline-block rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium text-blue-800"
                        : "ml-2 inline-block rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground"
                    }
                    data-testid="template-registry-row-source"
                    data-source-kind={deriveTemplateSource(row)}
                  >
                    {t(
                      `admin.templates.export_import.source.${deriveTemplateSource(row)}`,
                    )}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs">{row.description}</td>
                <td className="px-3 py-2 text-xs">{row.publisher_kind}</td>
                <td className="px-3 py-2 text-xs">
                  <PreVettedBadge row={row} />
                </td>
                <td className="px-3 py-2 text-xs text-muted-foreground">
                  {formatDate(new Date(row.published_at), locale)}
                </td>
                <td className="px-3 py-2 text-xs text-muted-foreground">
                  {row.published_by}
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <InstallButton
                      row={row}
                      onInstall={onInstall}
                      isInstalling={isInstallingRow === row.template_id}
                    />
                    {/* FR-UI-ADM-13 AC-1 — per-row Export action. */}
                    <ExportButton
                      templateID={row.template_id}
                      version={row.version}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {errCard ? (
        <div
          className="rounded border border-red-300 bg-red-50 p-3 text-xs text-red-700"
          role="alert"
          data-testid={errCard.testId}
        >
          {errCard.message}
        </div>
      ) : null}
    </div>
  );
}
